import type { CSSProperties, JSX, ReactNode } from 'react';
import type { Palette } from '../_theme/palette.js';

export type ShellLayout = 'modal' | 'drawer-right' | 'drawer-left' | 'sheet';

interface ShellProps {
  palette: Palette;
  onDismiss: () => void;
  layout?: ShellLayout;
  maxWidth?: number;
  children: ReactNode;
}

export function Shell({
  palette,
  onDismiss,
  layout = 'modal',
  maxWidth = 420,
  children,
}: ShellProps): JSX.Element {
  // Light dim only — no blur, because Chrome/Safari block backdrop-filter from
  // reaching host content across iframe boundaries (privacy/security). 0.3
  // opacity keeps the host page visible (light, dark, branded — all show
  // through), it just gets a subtle scrim so the panel contrast is readable.
  const backdropStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.3)',
    fontFamily: palette.font,
  };
  const containerStyle = resolveContainerStyle(layout, palette, maxWidth);

  return (
    <div role="dialog" aria-modal="true" onClick={onDismiss}>
      <style>{SHELL_KEYFRAMES}</style>
      <div style={backdropStyle} />
      <div onClick={(event) => event.stopPropagation()} style={containerStyle}>
        {children}
      </div>
    </div>
  );
}

function resolveContainerStyle(layout: ShellLayout, palette: Palette, maxWidth: number): CSSProperties {
  const base: CSSProperties = {
    position: 'fixed',
    width: '100%',
    background: palette.card,
    borderRadius: palette.radiusLg,
    boxShadow: '0 24px 48px rgba(15,23,42,0.35)',
    color: palette.text,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    maxHeight: 'calc(100vh - 48px)',
    maxWidth,
  };

  if (layout === 'drawer-right') {
    return {
      ...base,
      top: '50%',
      right: 24,
      transform: 'translateY(-50%)',
      animation: 'chirpSlideRight 220ms ease-out',
    };
  }
  if (layout === 'drawer-left') {
    return {
      ...base,
      top: '50%',
      left: 24,
      transform: 'translateY(-50%)',
      animation: 'chirpSlideLeft 220ms ease-out',
    };
  }
  if (layout === 'sheet') {
    return {
      ...base,
      bottom: 16,
      left: '50%',
      transform: 'translateX(-50%)',
      maxWidth: Math.max(maxWidth, 560),
      animation: 'chirpSlideUp 220ms ease-out',
    };
  }
  return {
    ...base,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    animation: 'chirpFadeIn 220ms ease-out',
  };
}

const SHELL_KEYFRAMES = `
@keyframes chirpSlideRight { from { transform: translate(24px, -50%); opacity: 0; } to { transform: translate(0, -50%); opacity: 1; } }
@keyframes chirpSlideLeft { from { transform: translate(-24px, -50%); opacity: 0; } to { transform: translate(0, -50%); opacity: 1; } }
@keyframes chirpSlideUp { from { transform: translate(-50%, 24px); opacity: 0; } to { transform: translate(-50%, 0); opacity: 1; } }
@keyframes chirpFadeIn { from { opacity: 0; transform: translate(-50%, calc(-50% + 8px)); } to { opacity: 1; transform: translate(-50%, -50%); } }
`;
